(() => {
  const wrapperSelector = '[data-bfs-cart-shipping]';
  let instances = [];

  const parseJson = (el) => {
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (error) {
      return null;
    }
  };

  const toNumber = (value) => {
    const num = typeof value === 'string' && value.trim() === '' ? NaN : Number(value);
    return Number.isFinite(num) ? num : null;
  };

  const interpolate = (template, vars) => {
    if (typeof template !== 'string') return '';
    return template.replace(/\{\{\s*(\w+)\s*\}\}/g, (_, key) => {
      return Object.prototype.hasOwnProperty.call(vars, key) ? vars[key] : '';
    });
  };

  const debounce = (fn, wait) => {
    let timer;
    return (...args) => {
      window.clearTimeout(timer);
      timer = window.setTimeout(() => fn(...args), wait);
    };
  };

  const track = (eventName, params) => {
    if (!Array.isArray(window.dataLayer)) return;
    window.dataLayer.push({ event: eventName, ...params });
  };

  let latestContext = null;
  let lastCheckoutTs = 0;

  const fetchCart = () => {
    return fetch('/cart.js', { credentials: 'same-origin' })
      .then((response) => (response.ok ? response.json() : null))
      .catch(() => null);
  };

  const formatCurrency = (amount, currency, locale, options = {}) => {
    if (!Number.isFinite(amount)) return '';
    if (!currency) return amount.toFixed(2);
    try {
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency,
        ...options,
      }).format(amount);
    } catch (error) {
      return amount.toFixed(2);
    }
  };

  const getZone = (rates, zoneKey) => {
    if (!rates || !zoneKey) return null;
    const zones = rates.zones || rates.zone_list || rates.zoneList;
    if (Array.isArray(zones)) {
      return (
        zones.find((zone) => zone.key === zoneKey) ||
        zones.find((zone) => zone.code === zoneKey) ||
        zones.find((zone) => zone.id === zoneKey)
      );
    }
    if (zones && typeof zones === 'object') {
      return zones[zoneKey] || zones[zoneKey.toUpperCase()] || zones[zoneKey.toLowerCase()] || null;
    }
    return null;
  };

  const getServiceCode = (service) => {
    return service?.code || service?.service_code || service?.serviceCode || service?.id || null;
  };

  const getServices = (zone) => {
    if (!zone) return [];
    const services = zone.services || zone.service_list || zone.serviceList;
    if (Array.isArray(services)) return services;
    if (services && typeof services === 'object') return Object.values(services);
    return [];
  };

  const getTierBounds = (tier) => {
    const min = toNumber(tier?.min ?? tier?.min_kg ?? tier?.min_weight ?? tier?.weight_min ?? tier?.from);
    const maxRaw = tier?.max ?? tier?.max_kg ?? tier?.max_weight ?? tier?.weight_max ?? tier?.to;
    const max = maxRaw === null ? null : toNumber(maxRaw);
    return { min: min ?? 0, max };
  };

  const selectTier = (tiers, weightKg) => {
    if (!Array.isArray(tiers) || tiers.length === 0) return null;
    const weight = Number.isFinite(weightKg) ? weightKg : 0;
    const direct = tiers.find((tier) => {
      const { min, max } = getTierBounds(tier);
      if (!Number.isFinite(min)) return false;
      if (max === null) return weight >= min;
      return weight >= min && weight <= max;
    });
    if (direct) return direct;

    const nextAvailable = tiers
      .map((tier) => ({ tier, min: getTierBounds(tier).min }))
      .filter((entry) => Number.isFinite(entry.min) && entry.min > weight)
      .sort((a, b) => a.min - b.min)[0];
    if (nextAvailable) return nextAvailable.tier;

    const openEnded = tiers.find((tier) => getTierBounds(tier).max === null);
    if (openEnded) return openEnded;

    return null;
  };

  const getAmountAndCurrency = (obj, fallbackCurrency) => {
    const amount = toNumber(obj?.amount ?? obj?.price ?? obj?.value ?? obj?.rate);
    const currency = obj?.currency || obj?.currency_code || obj?.currencyCode || fallbackCurrency || null;
    if (amount === null) return null;
    return { amount, currency };
  };

  const getCnDetails = (rates) => {
    const cnMode = rates?.special_modes?.CN || rates?.specialModes?.CN || {};
    const zh = cnMode?.details?.zh;
    return typeof zh === 'string' ? zh : '';
  };

  const EU = new Set([
    'AT',
    'BE',
    'BG',
    'HR',
    'CY',
    'CZ',
    'DK',
    'EE',
    'FI',
    'FR',
    'DE',
    'GR',
    'HU',
    'IE',
    'IT',
    'LV',
    'LT',
    'LU',
    'MT',
    'NL',
    'PL',
    'PT',
    'RO',
    'SK',
    'SI',
    'ES',
    'SE',
  ]);
  const CIS = new Set(['BY', 'KZ', 'KG', 'MD', 'UA', 'AM', 'UZ', 'RU']);

  const buildInstance = (wrapper) => {
    const sectionId = wrapper.dataset.sectionId || 'default';
    const locale = wrapper.dataset.locale || document.documentElement.lang || 'en';
    const countryIso = (wrapper.dataset.countryIso || '').toUpperCase();
    let hasRenderedOnce = false;
    let lastContext = {
      country: countryIso,
      mode: 'unknown',
      value_type: 'unknown',
      duties_notice_shown: false,
      weight_kg: null,
      currency: null,
    };

    const strings =
      parseJson(document.getElementById(`bfs-shipping-strings-${sectionId}`)) ||
      parseJson(wrapper.querySelector('[id^="bfs-shipping-strings-"]')) ||
      {};

    const rates =
      parseJson(document.getElementById(`bfs-shipping-rates-json-${sectionId}`)) ||
      parseJson(wrapper.querySelector('[id^="bfs-shipping-rates-json-"]')) ||
      parseJson(document.getElementById('bfs-shipping-rates-json')) ||
      null;

    const getEls = () => ({
      valueEl: wrapper.querySelector('[data-bfs-shipping-value]'),
      secondaryEl: wrapper.querySelector('[data-bfs-shipping-secondary]'),
      secondaryRowEl: wrapper.querySelector('[data-bfs-shipping-secondary-row]'),
      secondaryRightEl: wrapper.querySelector('[data-bfs-shipping-secondary-right]'),
      basedOnEl: wrapper.querySelector('[data-bfs-shipping-based-on]'),
      disclaimerEl: wrapper.querySelector('[data-bfs-shipping-disclaimer]'),
      dutiesEl: wrapper.querySelector('[data-bfs-shipping-duties]'),
      upsEl: wrapper.querySelector('[data-bfs-shipping-ups]'),
    });

    const t = (key, vars = {}) => {
      const template = strings[key] || '';
      return interpolate(template, vars);
    };

    const setSecondary = (text) => {
      const { secondaryEl } = getEls();
      if (!secondaryEl) return;
      if (text) {
        secondaryEl.hidden = false;
        secondaryEl.textContent = text;
      } else {
        secondaryEl.hidden = true;
        secondaryEl.textContent = '';
      }
    };

    const setSecondaryRight = (text) => {
      const { secondaryRowEl, secondaryRightEl } = getEls();
      if (!secondaryRowEl || !secondaryRightEl) return;
      if (text) {
        secondaryRowEl.hidden = false;
        secondaryRightEl.textContent = text;
      } else {
        secondaryRowEl.hidden = true;
        secondaryRightEl.textContent = '';
      }
    };

    const setBasedOn = (text) => {
      const { basedOnEl } = getEls();
      if (!basedOnEl) return;
      if (text) {
        basedOnEl.hidden = false;
        basedOnEl.textContent = text;
      } else {
        basedOnEl.hidden = true;
        basedOnEl.textContent = '';
      }
    };

    const setDutiesVisible = (visible) => {
      const { dutiesEl } = getEls();
      if (!dutiesEl) return;
      dutiesEl.hidden = !visible;
    };

    const updateContextAndTrack = ({ mode, value_type, currency, duties_notice_shown, weight_kg }) => {
      lastContext = {
        country: countryIso,
        mode,
        value_type,
        duties_notice_shown,
        weight_kg,
        currency: currency ?? null,
      };
      const eventBase = { ...lastContext };
      if (!hasRenderedOnce) {
        track('bfs_shipping_estimate_rendered', eventBase);
        hasRenderedOnce = true;
      } else {
        track('bfs_shipping_estimate_updated', eventBase);
      }
      latestContext = eventBase;
    };

    const renderFallback = () => {
      const { valueEl, disclaimerEl } = getEls();
      if (valueEl) valueEl.textContent = t('calculated_at_checkout') || 'Calculated at checkout';
      setSecondary('');
      setSecondaryRight('');
      setBasedOn('');
      if (disclaimerEl) disclaimerEl.textContent = t('exact_at_checkout') || 'Exact amount will be calculated at checkout.';
    };

    const renderLoading = () => {
      const { valueEl, disclaimerEl } = getEls();
      if (valueEl) valueEl.textContent = t('loading') || 'Calculating...';
      setSecondary('');
      setSecondaryRight('');
      setBasedOn('');
      if (disclaimerEl) disclaimerEl.textContent = t('exact_at_checkout') || 'Exact amount will be calculated at checkout.';
    };

    const renderWithData = (cart) => {
      if (!cart || cart.item_count === 0) {
        wrapper.hidden = true;
        return;
      }

      wrapper.hidden = false;
      const { valueEl, secondaryEl, disclaimerEl, dutiesEl } = getEls();

      setDutiesVisible(!EU.has(countryIso) && !CIS.has(countryIso) && countryIso !== 'US');
      const dutiesShown = !!(dutiesEl && dutiesEl.hidden === false);
      const weightKgForTracking = Number.isFinite(cart.total_weight)
        ? Math.round((cart.total_weight / 1000) * 100) / 100
        : null;

      if (countryIso === 'US') {
        renderFallback();
        updateContextAndTrack({
          mode: 'us_checkout_only',
          value_type: 'checkout_only',
          currency: null,
          duties_notice_shown: dutiesShown,
          weight_kg: weightKgForTracking,
        });
        return;
      }

      if (!rates || typeof rates !== 'object') {
        renderFallback();
        updateContextAndTrack({
          mode: 'fallback',
          value_type: 'checkout_only',
          currency: null,
          duties_notice_shown: dutiesShown,
          weight_kg: weightKgForTracking,
        });
        return;
      }

      const displayOnly = Array.isArray(rates.display_only_service_codes)
        ? rates.display_only_service_codes
        : Array.isArray(rates.displayOnlyServiceCodes)
          ? rates.displayOnlyServiceCodes
          : ['box'];

      const countryIndex = rates.country_index || rates.countryIndex || {};
      const zoneKey = countryIndex[countryIso] || countryIndex[countryIso.toLowerCase()] || null;
      const zone = getZone(rates, zoneKey);
      const modeRef = zone?.mode_ref || zone?.modeRef || null;

      // V etih zonah krome fiksirovannyh tarifov rabotaet UPS: ego summa
      // zavisit ot adresa i vesa i schitaetsya tolko v chekaute.
      const { upsEl } = getEls();
      if (upsEl) upsEl.hidden = !(zone && (zone.has_carrier || zone.hasCarrier));

      const isCN = countryIso === 'CN' || zoneKey === 'CHINA' || modeRef === 'CN';
      const isCIS = zoneKey === 'CIS' || modeRef === 'CIS';

      if (isCN) {
        if (valueEl) valueEl.textContent = t('payment_after_purchase') || 'Payment to the carrier after purchase';
        setSecondary(getCnDetails(rates));
        setSecondaryRight('');
        setBasedOn('');
        if (disclaimerEl) disclaimerEl.textContent = t('exact_at_checkout') || 'Exact amount will be calculated at checkout.';
        updateContextAndTrack({
          mode: 'cn',
          value_type: 'message',
          currency: null,
          duties_notice_shown: dutiesShown,
          weight_kg: weightKgForTracking,
        });
        return;
      }

      if (isCIS) {
        if (valueEl) valueEl.textContent = t('payment_after_purchase') || 'Payment to the carrier after purchase';

        const cisMode = rates.special_modes?.CIS || rates.specialModes?.CIS || {};
        const pricing = cisMode.pricing || {};
        const overrides = pricing.country_overrides || {};
        const perKgValue =
          toNumber(overrides[countryIso]) ??
          toNumber(overrides[countryIso.toLowerCase()]) ??
          toNumber(pricing.default_rate_per_kg);
        const cisCurrency = pricing.currency || 'EUR';

        let secondary = '';
        if (perKgValue !== null) {
          const perKgFormatted = formatCurrency(perKgValue, cisCurrency, locale, {
            minimumFractionDigits: 0,
            maximumFractionDigits: 2,
          });
          secondary = t('approx_per_kg', { price_per_kg: perKgFormatted }) || `≈ ${perKgFormatted}/kg`;

          const weightKg = Number.isFinite(cart.total_weight) ? cart.total_weight / 1000 : null;
          if (weightKg && weightKg > 0) {
            const totalApprox = formatCurrency(perKgValue * weightKg, cisCurrency, locale, {
              minimumFractionDigits: 0,
              maximumFractionDigits: 2,
            });
            const approxTotal = t('approx_total', { total: totalApprox }) || `≈ ${totalApprox}`;
            secondary = `${secondary} (${approxTotal})`;
          }
        }

        setSecondary('');
        setSecondaryRight(secondary);
        setBasedOn('');
        if (disclaimerEl) disclaimerEl.textContent = t('exact_at_checkout') || 'Exact amount will be calculated at checkout.';
        updateContextAndTrack({
          mode: 'cis',
          value_type: 'formula',
          currency: cisCurrency || null,
          duties_notice_shown: dutiesShown,
          weight_kg: weightKgForTracking,
        });
        return;
      }

      if (!zone) {
        renderFallback();
        updateContextAndTrack({
          mode: 'fallback',
          value_type: 'checkout_only',
          currency: null,
          duties_notice_shown: dutiesShown,
          weight_kg: weightKgForTracking,
        });
        return;
      }

      const services = getServices(zone);
      const service = services.find((item) => displayOnly.includes(getServiceCode(item))) || null;
      if (!service) {
        renderFallback();
        updateContextAndTrack({
          mode: 'fallback',
          value_type: 'checkout_only',
          currency: null,
          duties_notice_shown: dutiesShown,
          weight_kg: weightKgForTracking,
        });
        return;
      }

      const fallbackCurrency = service.currency || zone.currency || rates.currency || null;

      const tiers = Array.isArray(service.tiers) ? service.tiers : service.tiers ? Object.values(service.tiers) : [];
      const weightKg = Number.isFinite(cart.total_weight) ? cart.total_weight / 1000 : 0;
      const tierBounds = tiers.map((tier) => getTierBounds(tier));
      const hasOpenEndedTier = tierBounds.some((bound) => bound.max === null);
      const maxFinite = Math.max(
        ...tierBounds.map((bound) => (Number.isFinite(bound.max) ? bound.max : -Infinity))
      );
      if (
        !hasOpenEndedTier &&
        Number.isFinite(maxFinite) &&
        weightKg > maxFinite &&
        (rates.overflow_strategy === 'checkout' || service.overflow === 'checkout')
      ) {
        renderFallback();
        updateContextAndTrack({
          mode: 'fallback',
          value_type: 'checkout_only',
          currency: null,
          duties_notice_shown: dutiesShown,
          weight_kg: weightKgForTracking,
        });
        return;
      }
      const tier = selectTier(tiers, weightKg);

      if (!tier) {
        renderFallback();
        updateContextAndTrack({
          mode: 'fallback',
          value_type: 'checkout_only',
          currency: null,
          duties_notice_shown: dutiesShown,
          weight_kg: weightKgForTracking,
        });
        return;
      }

      const amountData = getAmountAndCurrency(tier, fallbackCurrency);
      if (!amountData) {
        renderFallback();
        updateContextAndTrack({
          mode: 'fallback',
          value_type: 'checkout_only',
          currency: null,
          duties_notice_shown: dutiesShown,
          weight_kg: weightKgForTracking,
        });
        return;
      }

      if (valueEl) {
        const approxPrefix = t('approx_prefix') || '≈ ';
        valueEl.textContent = `${approxPrefix}${formatCurrency(amountData.amount, amountData.currency, locale)}`;
      }
      setSecondary('');
      setSecondaryRight('');
      const fallbackText = t('calculated_at_checkout') || 'Calculated at checkout';
      if (valueEl && valueEl.textContent === fallbackText) {
        setBasedOn('');
      } else if (!secondaryEl || secondaryEl.hidden || secondaryEl.textContent === '') {
        setBasedOn(t('based_on_country_weight') || '');
      } else {
        setBasedOn('');
      }
      if (disclaimerEl) disclaimerEl.textContent = t('exact_at_checkout') || 'Exact amount will be calculated at checkout.';
      updateContextAndTrack({
        mode: 'regular',
        value_type: 'amount',
        currency: amountData.currency || null,
        duties_notice_shown: dutiesShown,
        weight_kg: weightKgForTracking,
      });
    };

    return {
      render: renderWithData,
      refresh: async () => {
        renderLoading();
        const cart = await fetchCart();
        renderWithData(cart || { item_count: 0 });
      },
    };
  };

  const ensureInstances = () => {
    const wrappers = Array.from(document.querySelectorAll(wrapperSelector));
    instances = wrappers.map((wrapper) => {
      if (!wrapper.__bfsInstance) {
        wrapper.__bfsInstance = buildInstance(wrapper);
      }
      return wrapper.__bfsInstance;
    });
  };

  const refreshAll = async () => {
    ensureInstances();
    const cart = await fetchCart();
    instances.forEach((instance) => instance.render(cart || { item_count: 0 }));
  };

  const debouncedRefreshAll = debounce(refreshAll, 250);

  document.addEventListener('cart:updated', (event) => {
    ensureInstances();
    const cart = event?.detail?.cart;
    if (cart && typeof cart.total_weight === 'number') {
      instances.forEach((instance) => instance.render(cart));
    }
    debouncedRefreshAll();
  });

  document.addEventListener('cart:refresh', () => {
    ensureInstances();
    debouncedRefreshAll();
  });

  document.addEventListener('change', (event) => {
    if (event.target && event.target.closest('main-cart')) {
      debouncedRefreshAll();
    }
  });

  document.addEventListener('click', (event) => {
    if (
      event.target &&
      event.target.closest('main-cart') &&
      event.target.closest('a[is="cart-remove-item"], .btn-remove, [data-cart-item-remove]')
    ) {
      debouncedRefreshAll();
    }
  });

  const observerTarget = document.querySelector('main-cart') || document.body;
  const debouncedRebind = debounce(() => {
    ensureInstances();
    refreshAll();
  }, 150);
  const observer = new MutationObserver((mutations) => {
    let shouldRefresh = false;
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (node.nodeType !== 1) continue;
        if (node.matches?.(wrapperSelector) || node.querySelector?.(wrapperSelector)) {
          shouldRefresh = true;
          break;
        }
      }
      if (shouldRefresh) break;
    }
    if (shouldRefresh) debouncedRebind();
  });
  if (observerTarget) {
    observer.observe(observerTarget, { childList: true, subtree: true });
  }

  document.addEventListener('click', (event) => {
    const checkoutButton = event.target?.closest('main-cart button[type="submit"][name="checkout"]');
    if (!checkoutButton) return;
    const now = Date.now();
    if (now - lastCheckoutTs < 1000) return;
    lastCheckoutTs = now;
    const ctx = latestContext || {};
    track('bfs_begin_checkout_from_cart', {
      country: ctx.country || null,
      mode: ctx.mode || 'unknown',
      value_type: ctx.value_type || 'unknown',
      duties_notice_shown: !!ctx.duties_notice_shown,
      weight_kg: typeof ctx.weight_kg === 'number' ? ctx.weight_kg : null,
      currency: ctx.currency ?? null,
    });
  });

  ensureInstances();
  instances.forEach((instance) => instance.refresh());
})();
